import React, { useState, useCallback } from 'react';
import { Check, AlertTriangle, Clock, Camera, User, FileText, Edit, RotateCcw, History, ChevronDown, ChevronUp } from 'lucide-react';
import { CompleteDeliveryFlow } from './CompleteDeliveryFlow';
import { IncompleteDeliveryFlow } from './IncompleteDeliveryFlow';
import { DeliveryPhoto } from './DeliveryPhoto';
import { DeliveryHistory } from './DeliveryHistory';
import { useToast } from '../../../hooks/useToast';

interface DeliveryRecord {
  id: string;
  status: 'delivered' | 'incomplete';
  receiver_name?: string;
  photos: string[];
  notes: string;
  reason?: 'manufacturer_fault' | 'delivery_fault';
  completed_by_name?: string;
  completed_at: string;
}

interface DeliveryWorkflowProps {
  jobId: string;
  deliveryResult: 'pending' | 'delivered' | 'incomplete';
  deliveryRecord: DeliveryRecord | null;
  onStatusChange?: () => void;
}

export const DeliveryWorkflow: React.FC<DeliveryWorkflowProps> = ({
  jobId,
  deliveryResult,
  deliveryRecord,
  onStatusChange,
}) => {
  const [activeFlow, setActiveFlow] = useState<'complete' | 'incomplete' | null>(null);
  const [showChoiceScreen, setShowChoiceScreen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const toast = useToast();

  // Reset delivery to go back to choice screen
  const handleResetDelivery = useCallback(() => {
    setActiveFlow(null);
    setShowChoiceScreen(true);
    toast.info('Delivery reset', 'You can now choose a different option');
  }, [toast]);

  // Handle flow completion
  const handleFlowComplete = useCallback(() => {
    setActiveFlow(null);
    setShowChoiceScreen(false);
    onStatusChange?.();
  }, [onStatusChange]);

  // Handle flow cancel
  const handleFlowCancel = useCallback(() => {
    setActiveFlow(null);
    // Don't reset showChoiceScreen on cancel, stay on choice screen if user came from reset
  }, []);

  // Show choice screen if user clicked "Change Choice" or if delivery is pending
  if (showChoiceScreen || (deliveryResult === 'pending' && !activeFlow)) {
    return (
      <div className="space-y-4">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Clock className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Delivery Pending</h3>
          <p className="text-gray-500 text-sm mt-1">
            Has this delivery been completed successfully?
          </p>
        </div>

        <button
          onClick={() => { setShowChoiceScreen(false); setActiveFlow('complete'); }}
          className="w-full py-4 bg-green-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm"
        >
          <Check className="w-6 h-6" />
          Complete Delivery
        </button>

        <button
          onClick={() => { setShowChoiceScreen(false); setActiveFlow('incomplete'); }}
          className="w-full py-4 bg-orange-50 text-orange-700 border-2 border-orange-300 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-orange-100 active:bg-orange-200 transition-colors"
        >
          <AlertTriangle className="w-6 h-6" />
          Incomplete Delivery
        </button>
        
        {showChoiceScreen && (
          <button
            onClick={() => setShowChoiceScreen(false)}
            className="w-full py-3 text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel and go back
          </button>
        )}
      </div>
    );
  }

  // Show status card for delivered
  if (deliveryResult === 'delivered' && !activeFlow) {
    return (
      <div className="space-y-4">
        {/* Status Card */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-green-900">Delivered</h3>
              {deliveryRecord?.receiver_name && (
                <p className="text-sm text-green-700 truncate">
                  Received by: {deliveryRecord.receiver_name}
                </p>
              )}
              {deliveryRecord?.completed_at && (
                <p className="text-xs text-green-600">
                  {new Date(deliveryRecord.completed_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Details Card */}
        {deliveryRecord && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            {/* Receiver */}
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Receiver</p>
                <p className="font-medium text-gray-900">{deliveryRecord.receiver_name}</p>
              </div>
            </div>

            {/* Photos */}
            {deliveryRecord.photos.length > 0 && (
              <div className="flex items-start gap-3">
                <Camera className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-2">Photos ({deliveryRecord.photos.length})</p>
                  <div className="grid grid-cols-4 gap-2">
                    {deliveryRecord.photos.map((photo, i) => (
                      <div
                        key={i}
                        className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
                      >
                        <DeliveryPhoto
                          jobId={jobId}
                          photoUrl={photo}
                          alt={`Photo ${i + 1}`}
                          className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Notes</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{deliveryRecord.notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* History Toggle */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <History className="w-4 h-4" />
          {showHistory ? 'Hide History' : 'View History'}
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {/* History Section */}
        {showHistory && <DeliveryHistory jobId={jobId} />}

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={() => setActiveFlow('complete')}
            className="w-full py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit Delivery Details
          </button>
          
          <button
            onClick={handleResetDelivery}
            className="w-full py-3 border border-orange-300 text-orange-700 bg-orange-50 rounded-lg font-medium hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Change Choice
          </button>
        </div>
      </div>
    );
  }

  // Show status card for incomplete
  if (deliveryResult === 'incomplete' && !activeFlow) {
    return (
      <div className="space-y-4">
        {/* Status Card */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-orange-900">Incomplete</h3>
              {deliveryRecord?.reason && (
                <p className="text-sm text-orange-700">
                  {deliveryRecord.reason === 'manufacturer_fault' 
                    ? 'Manufacturer Fault' 
                    : 'Delivery Fault'}
                </p>
              )}
              {deliveryRecord?.completed_at && (
                <p className="text-xs text-orange-600">
                  {new Date(deliveryRecord.completed_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Notes Card */}
        {deliveryRecord?.notes && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Description</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{deliveryRecord.notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* History Toggle */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <History className="w-4 h-4" />
          {showHistory ? 'Hide History' : 'View History'}
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {/* History Section */}
        {showHistory && <DeliveryHistory jobId={jobId} />}

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={() => setActiveFlow('incomplete')}
            className="w-full py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Edit Details
          </button>
          
          <button
            onClick={handleResetDelivery}
            className="w-full py-3 border border-orange-300 text-orange-700 bg-orange-50 rounded-lg font-medium hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Change Choice
          </button>
        </div>
      </div>
    );
  }

  // Show complete flow
  if (activeFlow === 'complete') {
    return (
      <CompleteDeliveryFlow
        jobId={jobId}
        existingData={deliveryRecord}
        onComplete={handleFlowComplete}
        onCancel={handleFlowCancel}
      />
    );
  }

  // Show incomplete flow
  if (activeFlow === 'incomplete') {
    return (
      <IncompleteDeliveryFlow
        jobId={jobId}
        onComplete={handleFlowComplete}
        onCancel={handleFlowCancel}
      />
    );
  }

  // Fallback - should not reach here
  return null;
};
